import { NgClass } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    forwardRef,
    inject,
    Input,
    OnInit,
    ViewChild
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbHighlight, NgbTypeahead } from "@ng-bootstrap/ng-bootstrap";
import {
    isObservable,
    merge,
    Observable,
    of,
    Subject
} from "rxjs";
import { filter } from "rxjs/internal/operators/filter";
import {
    debounceTime,
    distinctUntilChanged,
    map,
    switchMap,
    take,
    tap
} from "rxjs/operators";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { BusyDirective } from "../../directives/busy/busy.directive";
import { RequiredDirective } from "../../directives/required/required.directive";
import { ValidationMessageComponent } from "../../validation/validation-message/validation-message.component";
import { ControlValueAccessorBaseComponent } from "../base-component/control-value-accessor-base.component";
import { BusyService } from "../busy/busy.service";
import { InfoComponent } from "../info/info.component";

@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectorComponent)
        },
        {
            multi: false,
            provide: ControlValueAccessorBaseComponent,
            useExisting: forwardRef(() => SelectorComponent)
        }
    ],
    selector: "rask-selector",
    styleUrls: ["./selector.component.scss"],
    templateUrl: "./selector.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RequiredDirective,
        InfoComponent,
        FormsModule,
        NgbTypeahead,
        NgClass,
        BusyDirective,
        NgbHighlight,
        ValidationMessageComponent
    ]
})
export class SelectorComponent
    extends ControlValueAccessorBaseComponent
    implements OnInit, AfterViewInit, ControlValueAccessor {

    @Input() exclude = [] as any[];
    @Input() values: any;
    @Input() debounceTime = 0;
    @Input() minCharacters = 0;
    @Input() placeholder = "";
    @Input() canDelete = true;
    @Input() reverseLookup = true;
    @Input() groupBy: any;
    @Input() showHeader = false;
    @Input() addSearchTermToList = false;
    @Input() allowAnyValue = false;
    @Input() portalToBody = false;
    @Input() useItemIcons = false;

    @Input() popupClass = "";
    @Input() listPlacement = "bottom-left bottom-right top-left top-right"; // <- Default. Vilket håll listan öppnas.
    @Input() getSelectionFromModel: (value: any) => Observable<any>;
    @Input() addModelValueWhenMissing: (value: any) => any;
    @Input() viewSelectedFormatter = undefined;

    @Input() imgFormatter: (value: any) => any;

    @ViewChild(NgbTypeahead) instance: NgbTypeahead;
    @ViewChild("instance", { read: ElementRef }) input: ElementRef;

    focus$ = new Subject<string>();
    inputValue: any;
    busyIdentity: string;
    isFocused = false;
    currentValues: any[];
    currentSearch = "";
    useCache = false;

    private selectionComplete = false;

    get isPopupOpen(): boolean {
        return this.instance ? this.instance.isPopupOpen() : false;
    }
    private readonly busyService = inject(BusyService);

    constructor() {
        super();
        // eslint-disable-next-line sonarjs/pseudo-random
        this.busyIdentity = `selector + ${Math.random()
            .toString(36)
            .substring(2)}`;
    }

    @Input() search = (str: string) => new Observable<any>();
    @Input() viewFormatter = (i: any) => i;
    @Input() itemDisabled = (item: any) => false;

    onChange: any = () => {
        // Metoden måste finnas, även om den är tom, för att resterande kod ska lira som önskat
    };
    onTouched: any = () => {
        // Metoden måste finnas, även om den är tom, för att resterande kod ska lira som önskat
    };

    onInputChange($event) {
        if (this.addSearchTermToList) {
            const nextValues = this.values.getValue() ?? [this.inputValue];
            nextValues.push(this.inputValue);
            this.values.next(nextValues);
        } else if (this.allowAnyValue) {
            this.selectItem(this.inputValue);
        }
    }

    onGroupSelect(e: MouseEvent, item: any) {
        this.inputValue = item;
        this.value = item;
        this.selectionComplete = true;
        e.stopImmediatePropagation();
        this.instance.dismissPopup();
        this.input.nativeElement.blur();
    }

    onSelect = (e: any, close = true) => {
        if (!this.selectItem(e.item)) {
            e.preventDefault();
            return false;
        }

        return true;
    };

    onBlur = () => {
        if (!this.selectionComplete && this.inputValue !== "") {
            this.inputValue = this.value;
        } else if (this.inputValue === "") {
            this.value = undefined;
        }

        this.isFocused = false;
        this.focus.next(false);
    };

    onFocus = () => {
        this.isFocused = true;
        this.focus.next(true);
    };

    delete = () => {
        this.value = undefined;
        this.inputValue = undefined;
        this.input.nativeElement.focus();
    };

    selectItem(item: any, close = true) {
        if (this.itemDisabled(item)) {
            return false;
        }

        if (this.itemIsHeader(item)) {
            // Gör så att vi stänger popup om man klickar på en header.
            this.selectionComplete = true;
            this.input.nativeElement.blur();
            return false;
        }

        this.value = item;
        this.inputValue = this.value;
        this.selectionComplete = true;
        this.input.nativeElement.blur();

        if (!close) {
            this.input.nativeElement.focus();
        }

        return true;
    }

    itemIsHeader(item: any) {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (item && item.header) {
            return true;
        }

        return false;
    }

    invokeClick = () => {
        if (this.instance.isPopupOpen()) {
            return;
        }

        this.input.nativeElement.focus();
    };

    searchHandler = (text$: Observable<string>) => {
        // disabled control does not initialize static values
        // if (this.disabled) { return of(null); }

        const debouncedText$ = text$.pipe(
            debounceTime(this.debounceTime),
            distinctUntilChanged()
        );
        const inputFocus$ = this.focus$.pipe(map((f) => ""));

        return merge(debouncedText$, inputFocus$).pipe(
            filter((term) => term.length >= this.minCharacters),
            debounceTime(this.debounceTime),
            distinctUntilChanged(),
            tap(() => (this.selectionComplete = false)),
            tap((term) => (this.currentSearch = term)),
            switchMap((term) =>
                this.busyService.addWork(
                    this.runSearch(term),
                    this.busyIdentity
                )
            )
        );
    };

    runSearch = (term: string, filterResult = true) => {
        let worker: Observable<any>;
        if (this.useCache && this.currentValues) {
            worker = of(this.currentValues);
            this.useCache = false;
        } else if (this.values) {
            worker = this.parseValues().pipe(
                map((pl) =>
                    pl.filter(
                        (p) =>
                            this.viewFormatter(p)
                                .toString()
                                .toLowerCase()
                                .indexOf(term.toLowerCase()) > -1
                    )
                )
            );
        } else {
            worker = this.search(term);
        }

        return this.postProcessResult(worker, filterResult).pipe(
            tap((v) => {
                if (this.showHeader) {
                    v.unshift({ header: true });
                }

                this.currentValues = v;
            })
        );
    };

    postProcessResult(
        data: Observable<any>,
        filterResult: boolean
    ): Observable<any> {
        if (filterResult) {
            data = data.pipe(map((res) => this.filterSearch(res)));
        }

        if (this.groupBy) {
            data = data.pipe(map((d) => this.handleGroups(d)));
        }

        return data;
    }

    filterSearch(result: any[]) {
        return result.filter(
            (r) =>
                !this.exclude.some(
                    (e) => this.formatter(e) === this.formatter(r)
                )
        );
    }

    handleGroups(data: any[]): any[] {
        const groups = [];
        data.forEach((d) => {
            const key = this.groupBy(d);
            const current = groups[key];
            if (current === undefined) {
                groups[key] = { key: key,
                    values: [d] };
            } else {
                current.values.push(d);
            }
        });

        const result = Array.from({ length: groups.length });
        for (const key of Object.keys(groups)) {
            result.push(groups[key]);
        }

        return result;
    }

    parseValues(): Observable<any> {
        return isObservable(this.values) ? this.values : of(this.values);
    }

    writeValue(value: any): void {
        if (value === this.privateValue) {
            return;
        }

        if (value === null || value === undefined) {
            this.value = value;
            this.inputValue = value;
        } else if (this.reverseLookup) {
            this.setValueFromModel(value);
        } else {
            this.privateValue = value;
            this.inputValue = value;
        }
    }

    ngOnInit() {
        super.ngOnInit();
        if (!this.viewSelectedFormatter) {
            this.viewSelectedFormatter = this.viewFormatter;
        }
    }

    ngAfterViewInit() {
        this.overrideKeyDown();
        super.ngAfterViewInit();
    }

    overrideKeyDown() {
        const original = this.instance.handleKeyDown.bind(this.instance);
        this.instance.handleKeyDown = (e) => {
            if (!this.handleKeyDown(e)) {
                return false;
            }
            original(e);
            return undefined;
        };
    }

    handleKeyDown(e: KeyboardEvent) {
        // https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values
        switch (e.key) {
            case "Tab": // TAB
                this.instance.dismissPopup();
                return true;
            case "Enter": // Enter
                this.instance.dismissPopup();
                if (
                    this.currentSearch &&
                    this.currentSearch.length > 1 &&
                    this.currentValues.length > 0
                ) {
                    this.selectItem(this.currentValues[0]);
                }
                return false;
            case "ArrowUp": // Arrow up
                return !this.selectNext(-1);
            case "ArrowDown": // Arrow down
                return !this.selectNext();
        }

        return true;
    }

    selectNext(dir = 1) {
        if (!this.instance.isPopupOpen()) {
            return false;
        }

        const currentPos = this.currentValues.indexOf(this.value);
        let nextPos = currentPos + dir;
        if (nextPos >= this.currentValues.length) {
            nextPos = 0;
        }

        if (nextPos < 0) {
            nextPos = this.currentValues.length - 1;
        }

        this.useCache = true;
        this.selectItem(this.currentValues[nextPos], false);

        return true;
    }

    private setValueFromModel(value: any) {
        let worker: Observable<any>;
        if (this.getSelectionFromModel) {
            worker = this.getSelectionFromModel(value);
        } else {
            worker = this.runSearch("", false);
        }

        worker.pipe(
            take(1)
        ).subscribe((r) => {
            const list = r as any[];
            const found =
                list.find((i) => this.formatter(i) === value) ??
                this.fetchAndAddInitialValue(value);
            if (!found) {
                return;
            }

            this.privateValue = found;
            this.inputValue = found;
        });

        this.busyService.addWork(worker, this.busyIdentity);
    }

    private fetchAndAddInitialValue(value: any) {
        if (this.addModelValueWhenMissing) {
            const found = this.addModelValueWhenMissing(value);
            if (ObjectUtils.isNullOrUndefined(found)) {
                return undefined;
            }
            if (this.values) {
                this.values.push(found);
            }
            return found;
        }
        return undefined;
    }
}
